import { SiteUrls } from '@tableau/shared-urls';
import { DropdownButtonType, ListItem, SingleSelectDropdown } from '@tableau/widgets';
import React from 'react';
import { defaultMemoize } from 'reselect';

import { Messages } from '../../codegen/Localize';
import { Location } from '../../common/react/Location';
import { imgPath } from '../../common/utilities/ImgUrl';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ISiteNameSansId } from '../../ts/ServerApi/types/ISiteNameSansId';
import { ServerUrls } from '../ServerUrls';
import { NavPanelDisplayMode } from './NavPanel';
import { SiteFilteringList } from './SiteFilteringList';
import { SitePickerStyles } from './SitePickerStyles';

export interface SitePickerListeItem extends ListItem {
  siteUrlName: string;
}

export interface SitePickerOwnProps {
  readonly navPanelDisplayMode: NavPanelDisplayMode;
  readonly serverUrls: ServerUrls;
  readonly onSitePickerClick?: () => void;
  readonly onSelection: () => void;
}

export interface SitePickerStateProps {
  readonly currentSiteName: string;
  readonly sites: ReadonlyArray<ISiteNameSansId>;
  readonly inServerArea: boolean;
  readonly isSessionServerAdmin: boolean;
}

export interface SitePickerDispatchProps {
  requestSites: () => void;
}

export type SitePickerProps = SitePickerOwnProps & SitePickerStateProps & SitePickerDispatchProps;

export const SITE_PICKER_CHECK = imgPath('explorer-dropdown-check.svg');

function getSiteUrl(siteUrlName: string) {
  const queryString = window.location.search;
  const siteUrls = new SiteUrls({ siteUrlName, queryString: queryString });
  return FeatureFlags.homePage ? siteUrls.homeUrl() : siteUrls.exploreUrl();
}

function createSiteListItem(site: ISiteNameSansId): JSX.Element {
  // we have to create a new site urls since we are changing sites
  const url = getSiteUrl(site.urlName);

  /* eslint-disable jsx-a11y/alt-text */
  return (
    <a
      className={SitePickerStyles.ItemContainer}
      data-tb-test-id={`site-list-item-${site.name}`}
      href={url}
      title={site.name}
      tabIndex={-1}
    >
      <div className={SitePickerStyles.Item}>
        {site.name}
      </div>
    </a>
  );
}

function createSitesListItems(sites: ReadonlyArray<ISiteNameSansId>): SitePickerListeItem[] {
  return sites.map((site: ISiteNameSansId) => {
    return {
      siteUrlName: site.urlName,
      value: site.name,
      testId: `site-${site.name}`,
      content: createSiteListItem(site)
    };
  });
}

export class SitePicker extends React.Component<SitePickerProps> {

  private memoizedSitesListItems = defaultMemoize(createSitesListItems);

  public render(): JSX.Element {
    // test id for e2e test backward compat
    return (
      <SingleSelectDropdown
        children={this.getButtonContent()}
        type={DropdownButtonType.Text}
        list={this.getSiteList()}
        value={this.props.inServerArea ? null : this.props.currentSiteName}
        style={this.props.navPanelDisplayMode === 'icon' ? SitePickerStyles.SitePickerMinimized : SitePickerStyles.SitePickerExpanded}
        testId="site-switcher-button"
        onOpen={this.handleOpen}
        onClose={this.handleClose}
        onChange={this.handleChange}
        label={''}
        aria-controls={'site-picker-popup'}
      />
    );
  }

  private getSiteList = () => {
    const listItems = this.memoizedSitesListItems(this.props.sites);

    /* eslint-disable jsx-a11y/no-autofocus */
    return (
      <SiteFilteringList
        items={listItems}
        requestSites={this.props.requestSites}
        isSessionServerAdmin={this.props.isSessionServerAdmin}
        serverUrls={this.props.serverUrls}
        inServerArea={this.props.inServerArea}
        onManageAllSitesLinkClick={this.handleClose}

        autoFocus={true}
        searchPlaceholder={Messages.searchSites()}
        style={SitePickerStyles.List}

        // test ids for e2e test compatibility
        inputTestId="site-switcher-textbox-input"
        listTestId="site-picker-items"
        id="site-picker-popup"
      />
    );
  }

  private closePanel: () => void;

  private handleOpen = (done: () => void): void => {
    // save close panel for later
    this.closePanel = done;

    if (this.props.onSitePickerClick) {
      this.props.onSitePickerClick();
    }
  }

  private handleClose = (): void => {
    if (this.closePanel) {
      this.closePanel();
      this.closePanel = null;
    }

    this.props.onSelection();
  }

  private handleChange = (value: string) => {
    if (this.closePanel) {
      const items = this.memoizedSitesListItems(this.props.sites);
      const item = items.find((item) => item.value === value);
      if (item) {
        const url = getSiteUrl(item.siteUrlName);
        Location.instance.setUrl(url);
      }
    }

    this.handleClose();
  }

  private getButtonContent = () => {
    if (this.props.navPanelDisplayMode === 'icon') {
      return(
        <img
          src={imgPath('spaces_navigation_sites.svg')}
          width={18}
          height={18}
          alt={Messages.siteSwitcher_choose()}
        />
      );
    } else if (this.props.inServerArea) {
      return <span className={SitePickerStyles.SitePickerButton}>{Messages.siteSwitcher_allSites()}</span>;
    } else {
      return <span className={SitePickerStyles.SitePickerButton}>{this.props.currentSiteName}</span>;
    }
  }
}
